import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import {
  Table,
  Button,
  Card,
  Form,
  Input,
  Space,
  Typography,
  Spin,
  Select,
  DatePicker,
  Popconfirm,
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  CalendarOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { useStore } from '../../stores/root-store-context'
import { useNavigate } from 'react-router'

const { Option } = Select
const { RangePicker } = DatePicker

const CrmEventManagement: React.FC = observer(() => {
  const { t } = useTranslation()
  const { crmStore, applicationStore } = useStore()
  const navigate = useNavigate()

  const [filters, setFilters] = useState<{
    name?: string
    typeId?: number
    contactId?: number
    companyId?: number
    dateRange?: [moment.Moment, moment.Moment]
  }>({})

  useEffect(() => {
    Promise.all([
      crmStore.getCrmEvents(),
      crmStore.getCrmEventTypes(),
      crmStore.getContacts(),
      crmStore.getExternalCompanies(),
    ])
  }, [crmStore])

  const matchesContact = (ev: any, cid?: number) =>
    !cid || ev.contacts?.some((c: any) => c.id === cid)

  const matchesCompany = (ev: any, cid?: number) =>
    !cid || ev.externalCompanies?.some((c: any) => c.id === cid)

  const filteredEvents = crmStore.CrmEvents.filter(ev => {
    if (filters.name && !ev.name.toLowerCase().includes(filters.name.toLowerCase())) return false
    if (filters.typeId && ev.typeId !== filters.typeId) return false
    if (!matchesContact(ev, filters.contactId)) return false
    if (!matchesCompany(ev, filters.companyId)) return false
    if (filters.dateRange?.length === 2) {
      const [start, end] = filters.dateRange
      const evStart = moment(ev.dateStart)
      if (evStart.isBefore(start) || evStart.isAfter(end)) return false
    }
    return true
  })

  const columns = [
    { title: '#', dataIndex: 'id', key: 'id', width: '5%' },
    { title: t('Name'), dataIndex: 'name', key: 'name', width: '18%' },
    {
      title: t('Start'),
      dataIndex: 'dateStart',
      key: 'dateStart',
      width: '14%',
      render: (d: string) => moment(d).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: t('End'),
      dataIndex: 'dateEnd',
      key: 'dateEnd',
      width: '14%',
      render: (d: string) => moment(d).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: t('CrmEventType'),
      dataIndex: 'typeId',
      key: 'typeId',
      width: '10%',
      render: (id: number) => crmStore.CrmEventTypes.find(t => t.id === id)?.name || '',
    },
    {
      title: t('Contacts'),
      dataIndex: 'contacts',
      key: 'contacts',
      width: '15%',
      render: (arr: any[]) =>
        arr?.length
          ? arr
              .map(c => `${c.name} ${c.surname}`.trim())
              .slice(0, 2)
              .join(', ') + (arr.length > 2 ? ` +${arr.length - 2}` : '')
          : '',
    },
    {
      title: t('Companies'),
      dataIndex: 'externalCompanies',
      key: 'externalCompanies',
      width: '15%',
      render: (arr: any[]) =>
        arr?.length
          ? arr.map(c => c.name).slice(0, 2).join(', ') + (arr.length > 2 ? ` +${arr.length - 2}` : '')
          : '',
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: '9%',
      render: (_: unknown, record: any) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => navigate(`/${applicationStore.company?.Tenant}/crm/event/${record.id}`)}
          />
          <Popconfirm
            title={t('DeleteCrmEventConfirm')}
            onConfirm={() => crmStore.deleteCrmEvent(record.id)}
          >
            <Button danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <Spin spinning={crmStore.isLoading}>
      <Card
        extra={
          <Space>
            <Button
              icon={<CalendarOutlined />}
              onClick={() => navigate(`/${applicationStore.company?.Tenant}/crm/calendar`)}
            >
              {t('Calendar')}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate(`/${applicationStore.company?.Tenant}/crm/event/new`)}
            >
              {t('AddCrmEvent')}
            </Button>
          </Space>
        }
      >
        <Form layout="inline" style={{ marginBottom: 16 }} onValuesChange={(_, v) => setFilters(v)}>
          <Form.Item name="name">
            <Input placeholder={t('Name')} allowClear style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="typeId">
            <Select placeholder={t('CrmEventType')} allowClear style={{ width: 160 }}>
              {crmStore.CrmEventTypes.map(t => (
                <Option key={t.id} value={t.id}>
                  {t.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="contactId">
            <Select
              placeholder={t('Contact')}
              allowClear
              showSearch
              optionFilterProp="children"
              style={{ width: 180 }}
            >
              {crmStore.contacts.map(c => (
                <Option key={c.id} value={c.id}>
                  {c.name} {c.surname}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="companyId">
            <Select
              placeholder={t('Company')}
              allowClear
              showSearch
              optionFilterProp="children"
              style={{ width: 180 }}
            >
              {crmStore.externalCompanies.map(ec => (
                <Option key={ec.id} value={ec.id}>
                  {ec.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="dateRange">
            <RangePicker
              allowEmpty={[true, true]}
              style={{ width: 240 }}
              showTime
              format="DD/MM/YYYY HH:mm"
              placeholder={[t('Start'), t('End')]}
            />
          </Form.Item>
          <Button icon={<ReloadOutlined />} onClick={() => setFilters({})} title={t('Reset')} />
        </Form>

        <Table
          dataSource={filteredEvents}
          columns={columns}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          expandable={{
            expandedRowRender: r => (
              <div style={{ padding: 16 }}>
                <Typography.Text strong>{t('Description')}:</Typography.Text>
                <Typography.Paragraph>{r.description || t('NoDescription')}</Typography.Paragraph>
              </div>
            ),
          }}
        />
      </Card>
    </Spin>
  )
})

export default CrmEventManagement
